import React, { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import authAxios, { API } from '../../utils/authAxios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  Loader2, Send, Eye, RefreshCw, Mail, AlertTriangle, CheckCircle2,
  Users, Clock, XCircle, MousePointerClick, MailOpen, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

const CONFIRM_TEXT_INITIAL = 'ENVIAR REHABAPP';
const CONFIRM_TEXT_FOLLOWUP = 'ENVIAR FOLLOWUP';

const STRATEGY_OPTIONS = [
  { value: 'all_pending', label: 'Todos los pendientes (default)', hint: 'Comportamiento clásico: todo el que no recibió follow-up.' },
  { value: 'opened_no_click', label: 'Abrieron pero NO clickearon (hot leads)', hint: 'Priorizar el follow-up a quienes mostraron interés sin convertir.' },
  { value: 'not_opened', label: 'No abrieron el inicial', hint: 'Retest de subject / horario de envío.' },
  { value: 'opened_all', label: 'Todos los que abrieron (con o sin click)', hint: 'Todos los que mostraron interés inicial.' },
];

export default function AdminRehabAppBroadcast() {
  const [audience, setAudience] = useState(null);
  const [loadingAudience, setLoadingAudience] = useState(false);

  const [dryRunning, setDryRunning] = useState(false);
  const [dryResult, setDryResult] = useState(null);

  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [confirmInitial, setConfirmInitial] = useState('');

  const [followupDryRunning, setFollowupDryRunning] = useState(false);
  const [followupDryResult, setFollowupDryResult] = useState(null);

  const [followupSending, setFollowupSending] = useState(false);
  const [followupResult, setFollowupResult] = useState(null);
  const [confirmFollowup, setConfirmFollowup] = useState('');

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewKind, setPreviewKind] = useState('initial');
  const [previewHtml, setPreviewHtml] = useState('');

  // Tracking (genérico via /rehabapp/tracking)
  const [tracking, setTracking] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [followupStrategy, setFollowupStrategy] = useState('all_pending');

  const loadTracking = async () => {
    setTrackingLoading(true);
    try {
      const { data } = await authAxios.get(`${API}/admin/broadcast/rehabapp/tracking`);
      setTracking(data);
    } catch (e) {
      // Silencioso — es esperable que no haya tracking hasta que se envíe algo
      setTracking(null);
    } finally {
      setTrackingLoading(false);
    }
  };

  const loadAudience = async () => {
    setLoadingAudience(true);
    try {
      const { data } = await authAxios.get(`${API}/admin/broadcast/rehabapp/audience`);
      setAudience(data);
    } catch (e) {
      toast.error('No se pudo cargar audiencia: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoadingAudience(false);
    }
  };

  useEffect(() => { loadAudience(); loadTracking(); }, []);

  const runDryInitial = async () => {
    setDryRunning(true); setDryResult(null);
    try {
      const { data } = await authAxios.post(`${API}/admin/broadcast/rehabapp?dry_run=true`, {});
      setDryResult(data);
      toast.success(`Dry-run OK: ${data.valid_recipients} destinatarios válidos`);
    } catch (e) {
      toast.error('Error en dry-run: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setDryRunning(false);
    }
  };

  const runSendInitial = async () => {
    if (confirmInitial.trim() !== CONFIRM_TEXT_INITIAL) {
      toast.error(`Debes escribir exactamente: ${CONFIRM_TEXT_INITIAL}`);
      return;
    }
    setSending(true); setSendResult(null);
    try {
      const { data } = await authAxios.post(`${API}/admin/broadcast/rehabapp?dry_run=false`, {});
      setSendResult(data);
      toast.success(`Broadcast enviado: ${data.sent} emails · ${data.already_sent_skipped} ya enviados`);
      setConfirmInitial('');
      await Promise.all([loadAudience(), loadTracking()]);
    } catch (e) {
      toast.error('Error enviando: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSending(false);
    }
  };

  const runDryFollowup = async () => {
    setFollowupDryRunning(true); setFollowupDryResult(null);
    try {
      const { data } = await authAxios.post(
        `${API}/admin/broadcast/rehabapp/followup?dry_run=true&strategy=${encodeURIComponent(followupStrategy)}`,
        {}
      );
      setFollowupDryResult(data);
      toast.success(`Follow-up dry-run OK (${followupStrategy}): ${data.pending_total} elegibles`);
    } catch (e) {
      toast.error('Error dry-run followup: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setFollowupDryRunning(false);
    }
  };

  const runSendFollowup = async () => {
    if (confirmFollowup.trim() !== CONFIRM_TEXT_FOLLOWUP) {
      toast.error(`Debes escribir exactamente: ${CONFIRM_TEXT_FOLLOWUP}`);
      return;
    }
    setFollowupSending(true); setFollowupResult(null);
    try {
      const { data } = await authAxios.post(
        `${API}/admin/broadcast/rehabapp/followup?dry_run=false&strategy=${encodeURIComponent(followupStrategy)}`,
        {}
      );
      setFollowupResult(data);
      toast.success(`Follow-up (${followupStrategy}) enviado a ${data.sent} centros`);
      setConfirmFollowup('');
      await Promise.all([loadAudience(), loadTracking()]);
    } catch (e) {
      toast.error('Error enviando followup: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setFollowupSending(false);
    }
  };

  const openPreview = async (kind) => {
    setPreviewKind(kind);
    const file = kind === 'followup'
      ? '/email-preview-rehabapp-followup.html'
      : '/email-preview-rehabapp.html';
    try {
      const res = await fetch(file);
      const html = await res.text();
      setPreviewHtml(html);
      setPreviewOpen(true);
    } catch (e) {
      toast.error('No se pudo cargar preview');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-rehabapp-broadcast">
      {/* Header + Refresh */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
            <Mail className="w-6 h-6 text-teal-400" />
            Broadcast RehabApp
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Envío masivo del email de alianza SinAdicciones × RehabApp + follow-up automático a 3 días.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { loadAudience(); loadTracking(); }}
          disabled={loadingAudience || trackingLoading}
          className="border-slate-700 text-slate-300"
          data-testid="rehabapp-refresh-btn"
        >
          {(loadingAudience || trackingLoading) ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-2">Actualizar</span>
        </Button>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-slate-900 border-slate-800" data-testid="stat-valid-recipients">
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-1"><Users className="w-3 h-3" /> Destinatarios válidos</div>
            <p className="text-3xl font-bold text-teal-400">{audience?.valid_recipients ?? '—'}</p>
            <p className="text-xs text-slate-500 mt-1">de {audience?.total_centers ?? '—'} centros</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800" data-testid="stat-initial-sent">
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-1"><CheckCircle2 className="w-3 h-3" /> Iniciales enviados</div>
            <p className="text-3xl font-bold text-emerald-400">{audience?.initial_sent ?? '—'}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800" data-testid="stat-followup-pending">
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-1"><Clock className="w-3 h-3" /> Follow-ups pendientes</div>
            <p className="text-3xl font-bold text-amber-400">{audience?.followup_pending ?? '—'}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900 border-slate-800" data-testid="stat-followup-sent">
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-1"><CheckCircle2 className="w-3 h-3" /> Follow-ups enviados</div>
            <p className="text-3xl font-bold text-emerald-400">{audience?.followup_sent ?? '—'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tracking (Resend webhooks: opens / clicks / bounces) */}
      <Card className="bg-slate-900 border-slate-800" data-testid="rehabapp-tracking-card">
        <CardHeader>
          <CardTitle className="text-slate-100 flex items-center gap-2 text-lg">
            <TrendingUp className="w-5 h-5 text-cyan-400" /> Tracking (Resend webhooks)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!tracking || (tracking.totals?.initial_sent ?? 0) === 0 ? (
            <p className="text-slate-500 text-sm">
              Aún no hay envíos registrados. Los datos de aperturas/clicks aparecen aquí a medida que Resend envía los eventos por webhook.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="bg-slate-950/40 border border-slate-800 rounded-md p-3" data-testid="track-delivered">
                  <div className="text-xs text-slate-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Entregados</div>
                  <div className="text-2xl font-bold text-slate-100">{tracking.totals.delivered}</div>
                  <div className="text-xs text-slate-500">{tracking.rates.delivery_rate}%</div>
                </div>
                <div className="bg-slate-950/40 border border-slate-800 rounded-md p-3" data-testid="track-opened">
                  <div className="text-xs text-slate-400 flex items-center gap-1"><MailOpen className="w-3 h-3" /> Aperturas</div>
                  <div className="text-2xl font-bold text-emerald-400">{tracking.totals.opened}</div>
                  <div className="text-xs text-slate-500">{tracking.rates.open_rate}%</div>
                </div>
                <div className="bg-slate-950/40 border border-slate-800 rounded-md p-3" data-testid="track-clicked">
                  <div className="text-xs text-slate-400 flex items-center gap-1"><MousePointerClick className="w-3 h-3" /> Clicks CTA</div>
                  <div className="text-2xl font-bold text-teal-400">{tracking.totals.clicked}</div>
                  <div className="text-xs text-slate-500">{tracking.rates.click_rate}%</div>
                </div>
                <div className="bg-slate-950/40 border border-slate-800 rounded-md p-3" data-testid="track-bounced">
                  <div className="text-xs text-slate-400 flex items-center gap-1"><XCircle className="w-3 h-3" /> Bounces</div>
                  <div className="text-2xl font-bold text-rose-400">{tracking.totals.bounced}</div>
                  <div className="text-xs text-slate-500">{tracking.rates.bounce_rate}%</div>
                </div>
                <div className="bg-slate-950/40 border border-slate-800 rounded-md p-3" data-testid="track-followup-sent">
                  <div className="text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Follow-ups</div>
                  <div className="text-2xl font-bold text-amber-400">{tracking.totals.followup_sent}</div>
                  <div className="text-xs text-slate-500">enviados</div>
                </div>
              </div>

              {/* Tabla de destinatarios */}
              <div className="border border-slate-800 rounded-md max-h-80 overflow-y-auto" data-testid="rehabapp-recipients-table">
                <table className="w-full text-xs">
                  <thead className="bg-slate-950/70 sticky top-0">
                    <tr className="text-slate-400 text-left">
                      <th className="px-3 py-2 font-medium">Centro / Email</th>
                      <th className="px-3 py-2 font-medium">Estado</th>
                      <th className="px-3 py-2 font-medium">Abrió</th>
                      <th className="px-3 py-2 font-medium">Click</th>
                      <th className="px-3 py-2 font-medium">Follow-up</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(tracking.recipients || []).map((r, i) => {
                      const statusColor = {
                        clicked: 'text-teal-400',
                        opened: 'text-emerald-400',
                        delivered: 'text-slate-300',
                        sent: 'text-slate-400',
                        bounced: 'text-rose-400',
                        complained: 'text-rose-500',
                      }[r.status] || 'text-slate-400';
                      return (
                        <tr key={i} className="border-t border-slate-800/60 text-slate-300">
                          <td className="px-3 py-2">
                            <div className="text-slate-200">{(r.center_name || '').slice(0, 40) || '—'}</div>
                            <div className="text-[10px] text-slate-500">{r.email}</div>
                          </td>
                          <td className={`px-3 py-2 font-medium ${statusColor}`}>{r.status}</td>
                          <td className="px-3 py-2 text-slate-500">{r.opened_at ? '✓' : '—'}</td>
                          <td className="px-3 py-2 text-slate-500">{r.clicked_at ? '✓' : '—'}</td>
                          <td className="px-3 py-2 text-slate-500">{r.follow_up_sent ? '✓' : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Previews */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-slate-100 flex items-center gap-2 text-lg">
            <Eye className="w-5 h-5" /> Previsualizar templates
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={() => openPreview('initial')} className="border-slate-700 text-slate-300" data-testid="preview-initial-btn">
            <Eye className="w-4 h-4 mr-2" /> Ver email inicial
          </Button>
          <Button variant="outline" onClick={() => openPreview('followup')} className="border-slate-700 text-slate-300" data-testid="preview-followup-btn">
            <Eye className="w-4 h-4 mr-2" /> Ver email follow-up (3d)
          </Button>
        </CardContent>
      </Card>

      {/* Broadcast inicial */}
      <Card className="bg-slate-900 border-teal-800/50">
        <CardHeader>
          <CardTitle className="text-slate-100 flex items-center gap-2 text-lg">
            <Send className="w-5 h-5 text-teal-400" /> 1. Broadcast inicial
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-slate-950/50 border border-slate-800 rounded-md p-3 text-sm text-slate-400">
            Asunto: <span className="text-slate-200 font-medium">Presentamos RehabApp · Alianza SinAdicciones × RehabApp</span>
            <br />Cada envío programa automáticamente un follow-up a los 3 días.
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              onClick={runDryInitial}
              disabled={dryRunning}
              variant="outline"
              className="border-teal-700 text-teal-300 hover:bg-teal-500/10"
              data-testid="dryrun-initial-btn"
            >
              {dryRunning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
              Dry-run (listar sin enviar)
            </Button>
          </div>

          {dryResult && (
            <div className="bg-slate-950/60 border border-slate-800 rounded-md p-3 text-xs text-slate-300 max-h-56 overflow-y-auto" data-testid="dryrun-initial-result">
              <div className="mb-2 text-slate-100 font-medium">Dry-run: {dryResult.valid_recipients} destinatarios (bloqueados: {dryResult.blocked_count})</div>
              <ul className="space-y-1">
                {(dryResult.preview_recipients || []).map((r, i) => (
                  <li key={i}>• <span className="text-teal-300">{r.email}</span> — {r.name} <span className="text-slate-500">({r.city})</span></li>
                ))}
              </ul>
            </div>
          )}

          <div className="border-t border-slate-800 pt-4 space-y-3">
            <div className="flex items-start gap-2 text-amber-300 text-sm bg-amber-500/10 border border-amber-500/30 rounded-md p-3">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                Acción destructiva. Se enviará el email a <strong>{audience?.valid_recipients ?? '—'}</strong> centros reales.
                Escribe <code className="bg-slate-800 px-1 rounded text-amber-200">{CONFIRM_TEXT_INITIAL}</code> para confirmar.
              </div>
            </div>
            <input
              type="text"
              value={confirmInitial}
              onChange={(e) => setConfirmInitial(e.target.value)}
              placeholder={CONFIRM_TEXT_INITIAL}
              className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-md text-slate-100 placeholder:text-slate-600 font-mono"
              data-testid="confirm-initial-input"
            />
            <Button
              onClick={runSendInitial}
              disabled={sending || confirmInitial.trim() !== CONFIRM_TEXT_INITIAL}
              className="bg-teal-600 hover:bg-teal-700 disabled:opacity-40"
              data-testid="send-initial-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Enviar broadcast inicial ahora
            </Button>
          </div>

          {sendResult && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-md p-3 text-sm text-emerald-200" data-testid="send-initial-result">
              <div>Enviados: <strong>{sendResult.sent}</strong></div>
              <div>Ya enviados (skipped): <strong>{sendResult.already_sent_skipped}</strong></div>
              <div>Errores: <strong>{(sendResult.errors || []).length}</strong></div>
              <div className="text-xs text-emerald-300/70 mt-1">Broadcast ID: {sendResult.broadcast_id}</div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Follow-up */}
      <Card className="bg-slate-900 border-amber-800/50">
        <CardHeader>
          <CardTitle className="text-slate-100 flex items-center gap-2 text-lg">
            <Clock className="w-5 h-5 text-amber-400" /> 2. Follow-up manual (T+3 días)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-slate-950/50 border border-slate-800 rounded-md p-3 text-sm text-slate-400">
            Asunto: <span className="text-slate-200 font-medium">{'¿Alcanzaste a ver la propuesta RehabApp?'}</span>
            <br />El scheduler diario ({'12:00 UTC · ~09:00 CL'}) dispara esto automáticamente. Este botón es solo para pruebas o disparo manual.
          </div>

          {/* Selector de estrategia (usa el módulo genérico campaign_tracking) */}
          <div className="space-y-2">
            <label className="text-xs text-slate-400 uppercase tracking-wide font-medium">
              Estrategia de follow-up (basada en tracking de Resend)
            </label>
            <select
              value={followupStrategy}
              onChange={(e) => setFollowupStrategy(e.target.value)}
              className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-md text-slate-100"
              data-testid="followup-strategy-select"
            >
              {STRATEGY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <p className="text-xs text-slate-500">
              {STRATEGY_OPTIONS.find(o => o.value === followupStrategy)?.hint}
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              onClick={runDryFollowup}
              disabled={followupDryRunning}
              variant="outline"
              className="border-amber-700 text-amber-300 hover:bg-amber-500/10"
              data-testid="dryrun-followup-btn"
            >
              {followupDryRunning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
              Dry-run follow-up
            </Button>
          </div>

          {followupDryResult && (
            <div className="bg-slate-950/60 border border-slate-800 rounded-md p-3 text-xs text-slate-300 max-h-56 overflow-y-auto" data-testid="dryrun-followup-result">
              <div className="mb-2 text-slate-100 font-medium">Pendientes vencidos: {followupDryResult.pending_total}</div>
              <ul className="space-y-1">
                {(followupDryResult.preview_recipients || []).map((r, i) => (
                  <li key={i}>• <span className="text-amber-300">{r.email}</span> — {r.center_name} <span className="text-slate-500">(prog: {r.scheduled_at?.slice(0,16)})</span></li>
                ))}
              </ul>
            </div>
          )}

          <div className="border-t border-slate-800 pt-4 space-y-3">
            <input
              type="text"
              value={confirmFollowup}
              onChange={(e) => setConfirmFollowup(e.target.value)}
              placeholder={CONFIRM_TEXT_FOLLOWUP}
              className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-md text-slate-100 placeholder:text-slate-600 font-mono"
              data-testid="confirm-followup-input"
            />
            <Button
              onClick={runSendFollowup}
              disabled={followupSending || confirmFollowup.trim() !== CONFIRM_TEXT_FOLLOWUP}
              className="bg-amber-600 hover:bg-amber-700 disabled:opacity-40"
              data-testid="send-followup-btn"
            >
              {followupSending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Enviar follow-ups pendientes ahora
            </Button>
          </div>

          {followupResult && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-md p-3 text-sm text-emerald-200" data-testid="send-followup-result">
              <div>Enviados: <strong>{followupResult.sent}</strong> de {followupResult.pending_total}</div>
              <div>Errores: <strong>{(followupResult.errors || []).length}</strong></div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Modal */}
      {previewOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="preview-modal">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="sticky top-0 bg-slate-900 p-3 flex justify-between items-center">
              <h3 className="text-white font-medium">Preview: {previewKind === 'followup' ? 'Follow-up (3d)' : 'Inicial'}</h3>
              <Button variant="ghost" size="sm" onClick={() => setPreviewOpen(false)} className="text-white" data-testid="preview-close-btn">
                <XCircle className="w-5 h-5" />
              </Button>
            </div>
            <div
              className="overflow-y-auto p-4"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml, { ADD_ATTR: ['target', 'rel'] }) }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
